import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, List, ListItem, ListItemButton, ListItemText,
  ListItemIcon, Switch, Divider, Avatar, Button, TextField, IconButton, Tooltip, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Alert, InputAdornment, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Collapse, Chip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Notifications as NotificationsIcon, Security as SecurityIcon, DarkMode as DarkModeIcon,
  Logout as LogoutIcon, ArrowBack as ArrowBackIcon, Share as ShareIcon,
  ContentCopy as ContentCopyIcon, Save as SaveIcon, Edit as EditIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckIcon, RadioButtonUnchecked as UncheckedIcon,
  PersonAdd as PersonAddIcon, Mail as MailIcon,
  Group as GroupIcon, Add as AddIcon, SwapHoriz as SwapHorizIcon,
  Info as InfoIcon, Email as EmailIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, HelpOutline as HelpOutlineIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Routes, Route } from 'react-router-dom';
import { useMutation } from '@apollo/client/react';
import LockIcon from '@mui/icons-material/Lock';
import { PageHeader, MemberAvatar } from '../../components/common';
import { useAuth, useFamily, useThemeMode, useSubscription } from '../../context';
import { client } from '../../services/api/familypal/config';
import { UPDATE_PERSON, UPDATE_FAMILY_MEMBER, INSERT_FAMILY_MEMBER, UPDATE_FAMILY } from '@common/graphql/queries';
import { ClothingSizesDialog } from '../../components/dialogs';
import { usePersonData } from '../../hooks/usePersonData';
import { getOrySdk, handleOryError } from '../../services/ory';

const ROLES = ['Parent', 'Mom', 'Dad', 'Step-Parent', 'Guardian', 'Child', 'Son', 'Daughter', 'Other'];
const GENDERS = [
  { key: 'M', label: 'Male' },
  { key: 'F', label: 'Female' },
  { key: 'O', label: 'Other' },
];

// Member Detail Page - for editing a family member's profile
const MemberDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { memberId } = useParams<{ memberId: string }>();
  const { getMember, getMemberColor, getMemberRole, currentFamily, currentFamilyId } = useFamily();

  const member = memberId ? getMember(memberId) : null;
  const profile = member?.data?.profile;
  const memberData = memberId ? currentFamily?.data?.members?.[memberId] : null;
  const color = memberData?.color || getMemberColor(memberId || '');
  const role = memberData?.role || getMemberRole(memberId || '');

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [memberRole, setMemberRole] = useState('');
  const [memberColor, setMemberColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sizesDialogOpen, setSizesDialogOpen] = useState(false);

  const [updatePerson] = useMutation(UPDATE_PERSON);
  const [updateFamilyMember] = useMutation(UPDATE_FAMILY_MEMBER);

  // Clothing sizes data
  const {
    items: clothingSizesItems,
    insert: insertClothingSizes,
    update: updateClothingSizes,
  } = usePersonData(memberId, 'clothing_sizes', { useSubscription: false });

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first || '');
      setLastName(profile.last || '');
      setEmail(profile.email || '');
      setPhone(profile.phone || '');
      setGender(profile.gender || '');
      setBirthDate(profile.birthDate || '');
      setMemberRole(role || '');
      setMemberColor(color || '');
    }
  }, [profile, role, color]);

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Update person record
      await updatePerson({
        variables: {
          id: memberId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          gender: gender || null,
          birthDate: birthDate || null,
        },
      });

      // Update family member record (role, color)
      if (currentFamilyId && memberRole) {
        await updateFamilyMember({
          variables: {
            familyId: currentFamilyId,
            personId: memberId,
            role: memberRole,
            color: memberColor || null,
          },
        });
      }

      setSuccess(true);
      setEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!member) {
    return (
      <Box>
        <PageHeader title="Member Not Found" subtitle="The requested family member could not be found" />
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/settings')}>Back to Settings</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>Back</Button>

      <PageHeader
        title={`${profile?.first || ''} ${profile?.last || ''}`.trim() || 'Family Member'}
        subtitle={editing ? 'Editing profile' : 'Family member profile'}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Profile Photo & Basic Info */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <MemberAvatar
                profilePhotoId={profile?.profilePhotoId}
                name={`${firstName} ${lastName}`.trim()}
                color={memberColor || color}
                sx={{ width: 120, height: 120, fontSize: '2.5rem', mx: 'auto', mb: 2 }}
              />
              <Typography variant="h6">{firstName} {lastName}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {memberRole || role || 'Family Member'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                {!editing ? (
                  <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => setEditing(false)} disabled={saving}>
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Button size="small" onClick={() => setSizesDialogOpen(true)}>Clothing Sizes</Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Profile Information</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    disabled={!editing} required={editing} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)}
                    disabled={!editing} required={editing} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                    disabled={!editing} type="email" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                    disabled={!editing} type="tel" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {editing ? (
                    <FormControl fullWidth>
                      <InputLabel>Gender</InputLabel>
                      <Select value={gender} label="Gender" onChange={(e) => setGender(e.target.value)}>
                        <MenuItem value=""><em>Not set</em></MenuItem>
                        {GENDERS.map((g) => <MenuItem key={g.key} value={g.key}>{g.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField fullWidth label="Gender" disabled
                      value={gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : gender === 'O' ? 'Other' : ''} />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Birth Date" type={editing ? 'date' : 'text'}
                    value={birthDate} onChange={(e) => setBirthDate(e.target.value)}
                    disabled={!editing} InputLabelProps={editing ? { shrink: true } : undefined} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {editing ? (
                    <FormControl fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select value={memberRole} label="Role" onChange={(e) => setMemberRole(e.target.value)}>
                        {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField fullWidth label="Role" value={memberRole || role || ''} disabled />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)} message="Profile updated successfully" />

      {sizesDialogOpen && (
        <ClothingSizesDialog
          open={sizesDialogOpen}
          onClose={() => setSizesDialogOpen(false)}
          onSave={async (sizesData) => {
            const existing = clothingSizesItems[0];
            if (existing) {
              await updateClothingSizes(existing.data.id, sizesData);
            } else {
              await insertClothingSizes(sizesData);
            }
          }}
          personName={`${firstName} ${lastName}`.trim()}
          data={clothingSizesItems[0]?.data?.data || null}
        />
      )}
    </Box>
  );
};

// Invite Friends Card
const InviteCard: React.FC = () => {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralCount, setReferralCount] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Fetch referral code — URL is relative to SDK base (https://api.familypal.com/familypal/v1)
    client.get({ url: '/identity/referral/code' })
      .then((res: any) => {
        if (res.data) {
          setReferralCode(res.data.referral_code || res.data.referralCode || null);
          setReferralCount(res.data.referral_count || res.data.referralCount || 0);
        }
      })
      .catch(() => {
        // Fallback to stats endpoint
        client.get({ url: '/identity/referral/stats' })
          .then((res: any) => {
            if (res.data) {
              setReferralCode(res.data.referral_code || res.data.referralCode || null);
              setReferralCount(res.data.referral_count || res.data.referralCount || 0);
            }
          })
          .catch(() => {});
      });
  }, []);

  const inviteUrl = referralCode ? `https://app.familypal.com/invite/${referralCode}` : '';

  const handleCopy = () => {
    if (inviteUrl) {
      navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
    }
  };

  const handleShare = async () => {
    if (!inviteUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on FamilyPal!',
          text: `Join me on FamilyPal! ${inviteUrl}`,
          url: inviteUrl,
        });
      } catch {
        // User cancelled or share failed - fall back to copy
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ShareIcon color="primary" />
          <Typography variant="h6">Invite Friends</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Share your invite link with friends and family.
          {referralCount > 0 && ` ${referralCount} ${referralCount === 1 ? 'person has' : 'people have'} joined using your link.`}
        </Typography>
        {referralCode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={inviteUrl}
              slotProps={{ input: { readOnly: true } }}
              onClick={() => { navigator.clipboard.writeText(inviteUrl); setCopied(true); }}
              sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
            />
            <Tooltip title="Copy link">
              <IconButton onClick={handleCopy}><ContentCopyIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Share">
              <IconButton onClick={handleShare}><ShareIcon /></IconButton>
            </Tooltip>
          </Box>
        )}
        <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)} message="Invite link copied!" />
      </CardContent>
    </Card>
  );
};

// Color palette for family members
const MEMBER_COLORS = [
  '#96D0F0', '#F4A4B8', '#A8D8A8', '#F5C87A', '#C4A8E0',
  '#F0A88C', '#8CC8E8', '#E8B8D8', '#B8D8B0', '#D8C8A0',
];

// Add Member Dialog
const AddMemberDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  familyId: string;
  onSuccess: () => void;
}> = ({ open, onClose, familyId, onSuccess }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Child');
  const [color, setColor] = useState(MEMBER_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [insertFamilyMember] = useMutation(INSERT_FAMILY_MEMBER);

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await insertFamilyMember({
        variables: {
          familyId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim() || null,
          role,
          color,
        },
      });
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole('Child');
    setColor(MEMBER_COLORS[0]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Family Member</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          <TextField fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          <TextField fullWidth label="Email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Color</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {MEMBER_COLORS.map((c) => (
                <Box
                  key={c}
                  onClick={() => setColor(c)}
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                    border: color === c ? '3px solid' : '2px solid transparent',
                    borderColor: color === c ? 'primary.main' : 'transparent',
                    '&:hover': { opacity: 0.8 },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Adding...' : 'Add Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Create Family Dialog
const CreateFamilyDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: (familyId: string) => void;
}> = ({ open, onClose, onSuccess }) => {
  const [familyName, setFamilyName] = useState('');
  const [slug, setSlug] = useState('');
  const [role, setRole] = useState('Parent');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-generate slug from family name
  useEffect(() => {
    const generated = familyName
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/[ -]+/g, '-')
      .replace(/^-|-$/g, '');
    setSlug(generated);
  }, [familyName]);

  const handleCreate = async () => {
    if (!familyName.trim()) {
      setError('Family name is required');
      return;
    }
    if (!slug.trim()) {
      setError('Email handle is required');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      setError('Handle can only contain lowercase letters, numbers, and hyphens');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const resp = await fetch('https://api.familypal.com/familypal/v1/identity/family/setup', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: familyName.trim(),
          slug: slug.trim(),
          role,
        }),
      });

      if (!resp.ok) {
        const body = await resp.text();
        let message = `Failed to create family (${resp.status})`;
        try {
          const parsed = JSON.parse(body);
          if (parsed.message) message = parsed.message;
        } catch {}
        throw new Error(message);
      }

      const data = await resp.json();
      onSuccess(data.family_id || data.familyId);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create family');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFamilyName('');
    setSlug('');
    setRole('Parent');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Another Family</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Family Name"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            required
            placeholder="e.g. The Smith Family"
          />
          <TextField
            fullWidth
            label="Email Handle"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            required
            helperText={slug ? `Your family email will be ${slug}@familypal.com (cannot be changed later)` : 'This will be your family email address'}
            InputProps={{
              endAdornment: <InputAdornment position="end">@familypal.com</InputAdornment>,
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Your Role</InputLabel>
            <Select value={role} label="Your Role" onChange={(e) => setRole(e.target.value)}>
              {ROLES.filter(r => ['Parent', 'Mom', 'Dad', 'Step-Parent', 'Guardian'].includes(r))
                .map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating...' : 'Create Family'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Notification settings stored in localStorage
const NOTIFICATION_SETTINGS_KEY = 'familypal_notification_settings';

interface NotificationSettings {
  enabled: boolean;
  messages: boolean;
  calendar: boolean;
  activities: boolean;
  health: boolean;
  school: boolean;
  chores: boolean;
  money: boolean;
  custody: boolean;
  eventReminders: boolean;
  medicationReminders: boolean;
  choreReminders: boolean;
}

const defaultNotificationSettings: NotificationSettings = {
  enabled: true,
  messages: true,
  calendar: true,
  activities: true,
  health: true,
  school: true,
  chores: true,
  money: true,
  custody: true,
  eventReminders: true,
  medicationReminders: true,
  choreReminders: true,
};

// Notification Settings Card
const NotificationSettingsCard: React.FC = () => {
  const [settings, setSettings] = useState<NotificationSettings>(defaultNotificationSettings);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
      if (stored) {
        setSettings({ ...defaultNotificationSettings, ...JSON.parse(stored) });
      }
    } catch {}
  }, []);

  const updateSetting = useCallback((key: keyof NotificationSettings, value: boolean) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(newSettings));
  }, [settings]);

  const notificationsDisabled = !settings.enabled;

  const categoryItems: { key: keyof NotificationSettings; label: string; description: string }[] = [
    { key: 'messages', label: 'Messages', description: 'New messages in family chats' },
    { key: 'calendar', label: 'Calendar', description: 'Events added, updated, or cancelled' },
    { key: 'activities', label: 'Activities', description: 'Activities added or updated' },
    { key: 'health', label: 'Health', description: 'Medications, allergies, or records' },
    { key: 'school', label: 'School', description: 'Classes or school info' },
    { key: 'chores', label: 'Chores', description: 'Chores assigned or completed' },
    { key: 'money', label: 'Money', description: 'Allowances, payments, or expenses' },
    { key: 'custody', label: 'Custody', description: 'Swap requests or schedule changes' },
  ];

  const reminderItems: { key: keyof NotificationSettings; label: string; description: string }[] = [
    { key: 'eventReminders', label: 'Event Reminders', description: 'Remind before events start' },
    { key: 'medicationReminders', label: 'Medication Reminders', description: 'Reminders to take medications' },
    { key: 'choreReminders', label: 'Chore Reminders', description: 'Reminders for pending chores' },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Notifications</Typography>
        <List disablePadding>
          <ListItem>
            <ListItemIcon><NotificationsIcon /></ListItemIcon>
            <ListItemText primary="Push Notifications" secondary="Enable or disable all notifications" />
            <Switch checked={settings.enabled} onChange={(_, v) => updateSetting('enabled', v)} />
          </ListItem>
          <Divider />
          <ListItemButton onClick={() => setExpanded(!expanded)}>
            <ListItemIcon>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</ListItemIcon>
            <ListItemText primary="Category Settings" secondary="Configure which notifications you receive" />
          </ListItemButton>
        </List>
        <Collapse in={expanded}>
          <Box sx={{ pl: 2, pr: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>Categories</Typography>
            <List disablePadding dense>
              {categoryItems.map((item) => (
                <ListItem key={item.key} sx={{ opacity: notificationsDisabled ? 0.5 : 1 }}>
                  <ListItemText primary={item.label} secondary={item.description} />
                  <Switch
                    size="small"
                    checked={settings[item.key] as boolean}
                    onChange={(_, v) => updateSetting(item.key, v)}
                    disabled={notificationsDisabled}
                  />
                </ListItem>
              ))}
            </List>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>Reminders</Typography>
            <List disablePadding dense>
              {reminderItems.map((item) => (
                <ListItem key={item.key} sx={{ opacity: notificationsDisabled ? 0.5 : 1 }}>
                  <ListItemText primary={item.label} secondary={item.description} />
                  <Switch
                    size="small"
                    checked={settings[item.key] as boolean}
                    onChange={(_, v) => updateSetting(item.key, v)}
                    disabled={notificationsDisabled}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Family Management Card
const FamilyManagementCard: React.FC = () => {
  const navigate = useNavigate();
  const { currentFamily, currentFamilyId, setCurrentFamilyId, memberList, getMemberName, getMemberColor, getMemberRole, allFamilies, refetch } = useFamily();
  const { familySubscriptions } = useSubscription();

  const [editingName, setEditingName] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [createFamilyOpen, setCreateFamilyOpen] = useState(false);
  const [nameSuccess, setNameSuccess] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const [updateFamily] = useMutation(UPDATE_FAMILY);

  // Get slug from allFamilies (FamilyInfo includes slug)
  const familySlug = allFamilies.find((f) => f.id === currentFamilyId)?.slug;

  // Family name can be in data.name (JSONB) or allFamilies[].name (column)
  const currentFamilyName = currentFamily?.data?.name
    || allFamilies.find((f) => f.id === currentFamilyId)?.name
    || '';
  useEffect(() => {
    if (currentFamilyName && !editingName) {
      setFamilyName(currentFamilyName);
    }
  }, [currentFamilyName, editingName]);

  const handleSaveName = async () => {
    if (!familyName.trim() || !currentFamilyId) return;
    setSavingName(true);
    setNameError(null);
    try {
      const updatedData = { ...currentFamily?.data, name: familyName.trim() };
      await updateFamily({
        variables: { id: currentFamilyId, data: updatedData },
      });
      setEditingName(false);
      setNameSuccess(true);
      refetch();
    } catch (err: any) {
      setNameError(err.message || 'Failed to update family name');
    } finally {
      setSavingName(false);
    }
  };

  const handleMemberAdded = () => {
    refetch();
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <GroupIcon color="primary" />
            <Typography variant="h6">Family</Typography>
          </Box>

          {/* Family Switcher - shown when user has multiple families */}
          {allFamilies.length > 1 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Active Family</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={currentFamilyId || ''}
                  onChange={(e) => {
                    setCurrentFamilyId(e.target.value);
                    // Force a refetch after switching
                    setTimeout(() => refetch(), 100);
                  }}
                  startAdornment={<SwapHorizIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  {allFamilies.map((f) => {
                    const sub = familySubscriptions.find((fs) => fs.familyId === f.id);
                    const isActive = sub?.isActive ?? true; // default to active if status unknown
                    return (
                      <MenuItem key={f.id} value={f.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!isActive && <LockIcon fontSize="small" color="warning" />}
                            <span>{f.name}</span>
                          </Box>
                          <Chip
                            label={isActive ? 'Active' : 'Inactive'}
                            color={isActive ? 'success' : 'default'}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          )}

          {nameError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setNameError(null)}>{nameError}</Alert>}

          {/* Family Name */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Family Name</Typography>
            {editingName ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  fullWidth
                />
                <Button size="small" variant="contained" onClick={handleSaveName} disabled={savingName}>
                  {savingName ? 'Saving...' : 'Save'}
                </Button>
                <Button size="small" onClick={() => { setEditingName(false); setFamilyName(currentFamilyName); }}>
                  Cancel
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {currentFamilyName || 'No Family'}
                </Typography>
                <IconButton size="small" onClick={() => setEditingName(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Family Email */}
          {familySlug && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Family Email</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MailIcon fontSize="small" color="action" />
                <Typography variant="body1">{familySlug}@familypal.com</Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Members Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {memberList.length} {memberList.length === 1 ? 'Member' : 'Members'}
            </Typography>
            <Button
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddMemberOpen(true)}
            >
              Add Member
            </Button>
          </Box>

          {/* Family Members List */}
          <List disablePadding dense>
            {memberList.map((member) => {
              const name = getMemberName(member.id);
              const color = getMemberColor(member.id);
              const role = getMemberRole(member.id);
              return (
                <ListItemButton
                  key={member.id}
                  sx={{ borderRadius: 1, px: 1 }}
                  onClick={() => navigate(`/settings/member/${member.id}`)}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: color, fontSize: '0.8rem' }}>
                      {name.charAt(0)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={name}
                    secondary={role}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <Box
                    sx={{
                      width: 12, height: 12, borderRadius: '50%', bgcolor: color,
                      border: '1px solid', borderColor: 'divider', flexShrink: 0,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Create Another Family */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateFamilyOpen(true)}
            sx={{
              borderStyle: 'dashed',
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { borderStyle: 'dashed', borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            Create Another Family
          </Button>
        </CardContent>
      </Card>

      {currentFamilyId && (
        <AddMemberDialog
          open={addMemberOpen}
          onClose={() => setAddMemberOpen(false)}
          familyId={currentFamilyId}
          onSuccess={handleMemberAdded}
        />
      )}

      <CreateFamilyDialog
        open={createFamilyOpen}
        onClose={() => setCreateFamilyOpen(false)}
        onSuccess={(newFamilyId) => {
          setCurrentFamilyId(newFamilyId);
          refetch();
        }}
      />

      <Snackbar open={nameSuccess} autoHideDuration={3000} onClose={() => setNameSuccess(false)} message="Family name updated" />
    </>
  );
};

// FAQ Data (copied from mobile app)
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const faqData: FAQSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'How do I create a family?',
        answer: 'After signing up, you\'ll be guided through the family setup process. Simply enter your family name and add family members by providing their names and roles. You can invite other adults to join your family by sharing an invite link.',
      },
      {
        question: 'How do I invite family members?',
        answer: 'Go to Settings > Manage Families and tap "Invite Member." You can send an invite via email or share a link directly. When they accept, they\'ll be added to your family and can access shared information.',
      },
    ],
  },
  {
    title: 'Calendar & Scheduling',
    items: [
      {
        question: 'How do recurring events work?',
        answer: 'When creating an event, select a repeat option (Daily, Weekly, Bi-Weekly, Monthly, or Yearly). You can also specify which days of the week for weekly events and set an end date for the recurrence.',
      },
      {
        question: 'Can I edit a single occurrence of a recurring event?',
        answer: 'Yes! When you tap on a recurring event, you\'ll be asked if you want to edit just that occurrence or all events in the series. Choose "This Event" to modify only that specific date.',
      },
      {
        question: 'How does the custody calendar work?',
        answer: 'The custody feature allows you to set up a parenting schedule. Define your households, create a custody pattern (alternating weeks, specific days, etc.), and the calendar will automatically show which household each child is with.',
      },
      {
        question: 'How do I request a custody date swap?',
        answer: 'Go to Calendar > Custody Plans > Request Date Swap. Select the dates you want to swap on the calendar, add an optional reason, and send the request. The other parent will be notified and can approve or deny it.',
      },
      {
        question: 'What happens when a swap request is approved?',
        answer: 'Once approved, the swapped dates are automatically updated on the custody calendar. Both parents will see the change reflected immediately. The swap is recorded as an exception to the regular custody schedule.',
      },
      {
        question: 'Can I have multiple custody plans?',
        answer: 'Yes! You can create new custody plans with different date ranges. When you create a new plan that overlaps with an existing one, the current plan will be automatically ended the day before the new plan starts.',
      },
    ],
  },
  {
    title: 'Activities & School',
    items: [
      {
        question: 'How do I add an activity schedule?',
        answer: 'Go to Activities, tap the + button to create an activity, then add schedules for practices, games, lessons, etc. Each schedule can be recurring (e.g., every Tuesday) or a one-time event.',
      },
      {
        question: 'Are activity schedules added to the calendar?',
        answer: 'Yes! When you save an activity with schedules, corresponding calendar events are automatically created. If you update the activity schedule, the calendar events are updated too.',
      },
      {
        question: 'How do I track multiple children\'s classes?',
        answer: 'Each class can be assigned to a specific family member. Filter by child to see only their classes, or view all classes together. You can also link classes to schools in your contacts.',
      },
    ],
  },
  {
    title: 'Health & Medical',
    items: [
      {
        question: 'Is health information secure?',
        answer: 'Yes. All health data (medications, allergies, illnesses) is encrypted and stored securely. Only family members you\'ve invited can see this information.',
      },
      {
        question: 'Can I track medication schedules?',
        answer: 'You can add medications with dosage information and notes. For medication reminders, create calendar events linked to the medication for scheduling doses.',
      },
    ],
  },
  {
    title: 'Messaging & Communication',
    items: [
      {
        question: 'Who can see family messages?',
        answer: 'Messages in a family chat are visible to all members of that family. You can also create direct messages between specific family members or parent-only chats that children cannot see.',
      },
      {
        question: 'Can I share photos in messages?',
        answer: 'Yes! Tap the attachment icon to share photos, videos, or files. Shared media is stored securely and can be viewed by all chat participants.',
      },
    ],
  },
  {
    title: 'Account & Settings',
    items: [
      {
        question: 'How do I change my password?',
        answer: 'Go to Settings > Edit Profile and select "Change Password." You\'ll need to enter your current password and then set a new one.',
      },
      {
        question: 'How do I delete my account?',
        answer: 'Contact us at support@familypal.com to request account deletion. We\'ll verify your identity and process the request within 30 days. Note that this will remove all your data from our systems.',
      },
      {
        question: 'Can I export my data?',
        answer: 'Yes. Contact support to request a data export. We\'ll provide you with a complete copy of your family\'s data in a standard format.',
      },
    ],
  },
];

// FAQ Card
const FAQCard: React.FC = () => {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <QuestionAnswerIcon color="primary" />
          <Typography variant="h6">Frequently Asked Questions</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Find answers to commonly asked questions about FamilyPal. Can't find what you're looking for? Contact us at support@familypal.com
        </Typography>

        {faqData.map((section) => (
          <Box key={section.title} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {section.title}
            </Typography>
            {section.items.map((item, idx) => (
              <Accordion
                key={idx}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:not(:last-child)': { borderBottom: 0 },
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={500}>{item.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

// Main Settings Page
const MainSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { personId } = useFamily();
  const { mode, toggleTheme } = useThemeMode();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box>
      <PageHeader title="Settings" subtitle="Manage your account and preferences" />

      <Grid container spacing={3}>
        {/* Profile Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Profile</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                  {user?.name?.charAt(0) || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>{user?.name || 'User'}</Typography>
                  <Typography variant="body2" color="text.secondary">{user?.email || ''}</Typography>
                </Box>
              </Box>
              {personId && (
                <Button variant="outlined" size="small" onClick={() => navigate(`/settings/member/${personId}`)}>
                  Edit Profile
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Family Management Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <FamilyManagementCard />
        </Grid>

        {/* Preferences */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Preferences</Typography>
              <List disablePadding>
                <ListItem>
                  <ListItemIcon><DarkModeIcon /></ListItemIcon>
                  <ListItemText primary="Dark Mode" secondary="Use dark theme" />
                  <Switch checked={mode === 'dark'} onChange={toggleTheme} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid size={12}>
          <NotificationSettingsCard />
        </Grid>

        {/* Invite Friends */}
        <Grid size={12}>
          <InviteCard />
        </Grid>

        {/* Security */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Security</Typography>
              <List disablePadding>
                <ListItemButton onClick={() => navigate('/settings/password')}>
                  <ListItemIcon><SecurityIcon /></ListItemIcon>
                  <ListItemText primary="Change Password" secondary="Update your password" />
                </ListItemButton>
                <Divider />
                <ListItemButton onClick={() => navigate('/settings/two-factor')}>
                  <ListItemIcon><SecurityIcon /></ListItemIcon>
                  <ListItemText primary="Two-Factor Authentication" secondary="Secure your account with TOTP" />
                </ListItemButton>
                <Divider />
                <ListItemButton onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Log Out" secondary="Sign out of your account" primaryTypographyProps={{ color: 'error' }} />
                </ListItemButton>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* FAQ */}
        <Grid size={12}>
          <FAQCard />
        </Grid>

        {/* Support & About */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Support</Typography>
              <List disablePadding>
                <ListItemButton onClick={() => window.open('mailto:support@familypal.com', '_blank')}>
                  <ListItemIcon><EmailIcon /></ListItemIcon>
                  <ListItemText primary="Contact Us" secondary="support@familypal.com" />
                </ListItemButton>
                <Divider />
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText
                    primary="About"
                    secondary="FamilyPal Web v2.0.0"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Change Password Page
const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [flowId, setFlowId] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Initialize browser settings flow
  useEffect(() => {
    const initFlow = async () => {
      setLoading(true);
      setError(null);
      try {
        const sdk = getOrySdk();
        const response = await sdk.createBrowserSettingsFlow();
        setFlowId(response.data.id);
      } catch (err) {
        setError(handleOryError(err));
      } finally {
        setLoading(false);
      }
    };
    initFlow();
  }, []);

  const validate = (): string | null => {
    if (!currentPassword) return 'Please enter your current password.';
    if (!newPassword) return 'Please enter a new password.';
    if (newPassword.length < 8) return 'New password must be at least 8 characters.';
    if (newPassword !== confirmPassword) return 'New passwords do not match.';
    if (currentPassword === newPassword) return 'New password must be different from current.';
    return null;
  };

  const handleSubmit = async () => {
    if (!flowId) return;
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSubmitting(true);
    setError(null);

    try {
      const sdk = getOrySdk();
      await sdk.updateSettingsFlow({
        flow: flowId,
        updateSettingsFlowBody: {
          method: 'password',
          password: newPassword,
        },
      });
      setSuccess(true);
    } catch (err: any) {
      if (err?.response?.status === 410 || err?.response?.status === 404) {
        setError('Session expired. Please try again.');
        setFlowId(null);
      } else if (err?.response?.data?.ui) {
        const messages = err.response.data.ui.messages || [];
        const nodeMessages = err.response.data.ui.nodes?.flatMap((n: any) => n.messages || []) || [];
        const all = [...messages, ...nodeMessages];
        setError(all.length > 0 ? all.map((m: any) => m.text).join('\n') : handleOryError(err));
      } else {
        setError(handleOryError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const passLengthOk = newPassword.length >= 8;
  const passMatch = newPassword === confirmPassword && newPassword.length > 0;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/settings')} sx={{ mb: 2 }}>Back</Button>
      <PageHeader title="Change Password" subtitle="Update your account password" />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3} justifyContent="center">
          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>Password changed successfully.</Alert>}

            <Card>
              <CardContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Choose a strong password with at least 8 characters.
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <TextField
                    label="Current Password"
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => { setCurrentPassword(e.target.value); setError(null); }}
                    fullWidth
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end" size="small">
                            {showCurrent ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="New Password"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
                    fullWidth
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowNew(!showNew)} edge="end" size="small">
                            {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Confirm New Password"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                    fullWidth
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" size="small">
                            {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Requirements */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">Password Requirements:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    {passLengthOk ? <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} /> : <UncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                    <Typography variant="caption" color={passLengthOk ? 'success.main' : 'text.secondary'}>At least 8 characters</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {passMatch ? <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} /> : <UncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                    <Typography variant="caption" color={passMatch ? 'success.main' : 'text.secondary'}>Passwords match</Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 3 }}
                  onClick={handleSubmit}
                  disabled={submitting || success}
                >
                  {submitting ? <CircularProgress size={24} /> : success ? 'Password Changed' : 'Change Password'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

// Two-Factor Authentication Page
const TwoFactorPage: React.FC = () => {
  const navigate = useNavigate();

  const [flowId, setFlowId] = useState<string | null>(null);
  const [totpQr, setTotpQr] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const parseFlowNodes = (flow: any) => {
    setFlowId(flow.id);
    let qr: string | null = null;
    let secret: string | null = null;
    let enrolled = false;

    for (const node of flow.ui?.nodes || []) {
      const attrs = node.attributes || {};
      if (attrs.id === 'totp_qr' && attrs.src) {
        qr = attrs.src;
      }
      if (attrs.id === 'totp_secret_key' || node.meta?.label?.id === 1050006) {
        secret = attrs.text?.text || node.meta?.label?.text || '';
      }
      if (attrs.name === 'totp_unlink') {
        enrolled = true;
      }
    }

    setTotpQr(qr);
    setTotpSecret(secret);
    setIsEnrolled(enrolled);
  };

  useEffect(() => {
    const initFlow = async () => {
      setLoading(true);
      setError(null);
      try {
        const sdk = getOrySdk();
        const response = await sdk.createBrowserSettingsFlow();
        parseFlowNodes(response.data);
      } catch (err) {
        setError(handleOryError(err));
      } finally {
        setLoading(false);
      }
    };
    initFlow();
  }, []);

  const handleEnable = async () => {
    if (!flowId || !code) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const sdk = getOrySdk();
      const response = await sdk.updateSettingsFlow({
        flow: flowId,
        updateSettingsFlowBody: { method: 'totp', totp_code: code } as any,
      });
      parseFlowNodes(response.data);
      setSuccess('Two-factor authentication enabled successfully.');
      setCode('');
    } catch (err: any) {
      if (err?.response?.data?.ui) {
        const messages = err.response.data.ui.messages || [];
        const nodeMessages = err.response.data.ui.nodes?.flatMap((n: any) => n.messages || []) || [];
        const all = [...messages, ...nodeMessages];
        setError(all.length > 0 ? all.map((m: any) => m.text).join('\n') : handleOryError(err));
        if (err.response.data.id) parseFlowNodes(err.response.data);
      } else {
        setError(handleOryError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (!flowId) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const sdk = getOrySdk();
      const response = await sdk.updateSettingsFlow({
        flow: flowId,
        updateSettingsFlowBody: { method: 'totp', totp_unlink: true } as any,
      });
      parseFlowNodes(response.data);
      setSuccess('Two-factor authentication has been disabled.');
    } catch (err: any) {
      if (err?.response?.data?.ui) {
        const messages = err.response.data.ui.messages || [];
        const nodeMessages = err.response.data.ui.nodes?.flatMap((n: any) => n.messages || []) || [];
        const all = [...messages, ...nodeMessages];
        setError(all.length > 0 ? all.map((m: any) => m.text).join('\n') : handleOryError(err));
      } else {
        setError(handleOryError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/settings')} sx={{ mb: 2 }}>Back</Button>
      <PageHeader title="Two-Factor Authentication" subtitle="Add an extra layer of security to your account" />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3} justifyContent="center">
          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Card>
              <CardContent>
                {isEnrolled ? (
                  <Box>
                    <Alert severity="success" icon={<SecurityIcon />} sx={{ mb: 3 }}>
                      Two-factor authentication is enabled on your account.
                    </Alert>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Your account is protected with an authenticator app. You will be asked for a verification code when signing in.
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={handleDisable}
                      disabled={submitting}
                    >
                      {submitting ? <CircularProgress size={24} /> : 'Disable Two-Factor Authentication'}
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      Scan the QR code below with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
                    </Alert>

                    {totpQr && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                          component="img"
                          src={totpQr}
                          alt="TOTP QR Code"
                          sx={{ width: 200, height: 200, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                        />
                      </Box>
                    )}

                    {totpSecret && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Or enter this secret manually:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 1.5, py: 0.75, borderRadius: 1, flex: 1, wordBreak: 'break-all' }}
                          >
                            {totpSecret}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { navigator.clipboard.writeText(totpSecret); }}
                          >
                            Copy
                          </Button>
                        </Box>
                      </Box>
                    )}

                    <TextField
                      label="Verification Code"
                      value={code}
                      onChange={(e) => { setCode(e.target.value); setError(null); }}
                      fullWidth
                      placeholder="Enter 6-digit code"
                      inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                      sx={{ mb: 2 }}
                    />

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleEnable}
                      disabled={submitting || code.length < 6}
                    >
                      {submitting ? <CircularProgress size={24} /> : 'Enable Two-Factor Authentication'}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

// Router wrapper for settings pages
const SettingsPage: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<MainSettingsPage />} />
      <Route path="/member/:memberId" element={<MemberDetailPage />} />
      <Route path="/password" element={<ChangePasswordPage />} />
      <Route path="/two-factor" element={<TwoFactorPage />} />
    </Routes>
  );
};

export default SettingsPage;
